import { useState } from "react"
import { useTranslation } from "react-i18next"
import { Box, Text } from "@chakra-ui/react"
import { colors } from "../../../utilities/colors"
import { useIsMobile } from "../../../hooks/useIsMobile"
import QuoteSummary from "./QuoteSummary"
import PriceOptionCard from "../shared/PriceOptionCard"
import CreateOrderPage from "../Orders/CreateOrderPage"
import { locationTypeLabels } from "../../../constants/carpoolLocation"

export default function QuoteResult({ quote }) {
  const { t } = useTranslation()
  const isMobile = useIsMobile()
  const carpoolQuote = quote.attributes['response-cache']
  const priceOptions = quote.attributes['price-options']
  const [selectedPriceOption, setSelectedPriceOption] = useState(null)

  const sanitizeLocationData = (data) => {
    if (!data) return null
    return {
      name: data['dealer-name'] || data.name || '',
      address: data.address1 || data.address || '',
      city: data.city || '',
      state: data.state || '',
      postal_code: data.zip || data.postal_code || '',
    }
  }

  const fallbackLocation = (loc, locationType) => {
    if (!loc) return null
    const typeLabel = locationType ? t(locationTypeLabels[locationType] || locationType) : null
    return {
      name: typeLabel ? t('Custom {{type}} Address', { type: typeLabel }) : t('Custom Address'),
      address: t('Address to be filled in later'),
      city: loc.city || '',
      state: loc.state || '',
      postal_code: loc.postal_code || '',
    }
  }

  const originLocationType = quote.attributes['origin-location-type']
  const destinationLocationType = quote.attributes['destination-location-type']
  const origin = sanitizeLocationData(quote.attributes.origin) || fallbackLocation(carpoolQuote?.origin, originLocationType)
  const destination = sanitizeLocationData(quote.attributes.destination) || fallbackLocation(carpoolQuote?.destination, destinationLocationType)

  const createdBy = quote.attributes['created-by']
  const isQuoteValid = carpoolQuote?.status === 'active'
  const expiresAt = carpoolQuote?.expires_at
    ? new Date(carpoolQuote.expires_at).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' })
    : null

  if (!carpoolQuote) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" w="full" py={10}>
        <Text fontSize="sm" color={colors.grey300}>
          {t('Quote details are not yet available.')}
        </Text>
      </Box>
    )
  }

  return (
    <Box display="flex" flexDirection="column" gap={5} w="full" py={2}>
      {(createdBy || expiresAt) && (
        <Box display="flex" flexDirection="column" gap={1}>
          {createdBy && (
            <Box display="flex" justifyContent="space-between" alignItems="center" gap={2}>
              <Text fontSize="xs" color={colors.grey300}>{t('Created by')}</Text>
              <Text fontSize="xs" color={colors.grey300} minWidth={0} truncate>{createdBy}</Text>
            </Box>
          )}
          {expiresAt && (
            <Box display="flex" justifyContent="space-between" alignItems="center" gap={2}>
              <Text fontSize="xs" color={colors.grey300}>{t('Expires on')}</Text>
              <Text fontSize="xs" color={isQuoteValid ? colors.grey300 : colors.fpxRed}>{expiresAt}</Text>
            </Box>
          )}
        </Box>
      )}
      <QuoteSummary
        origin={origin}
        destination={destination}
        vehicles={carpoolQuote.vehicles || []}
        priceOptions={priceOptions}
      />
      <Text fontSize="md" fontWeight="semibold" color={colors.grey100} textAlign="center">
        {t('Select a Shipping Option')}
      </Text>
      <Box display="flex" flexDirection={{ base: 'column', md: 'row' }} gap={4}>
        {priceOptions?.map((option) => (
          <PriceOptionCard
            key={option.type}
            option={option}
            horizontalOrientation={isMobile}
            vehicleCount={carpoolQuote.vehicles?.length || 1}
            onClick={() => setSelectedPriceOption(option.type)}
          />
        ))}
      </Box>
      <CreateOrderPage
        isOpen={!!selectedPriceOption}
        onClose={() => setSelectedPriceOption(null)}
        selectedPriceOption={selectedPriceOption}
      />
    </Box>
  )
}
